<template>
  <b-container fluid class="inbox-page py-3">
    <div class="iq-card">
      <div class="iq-card-header inbox-header py-2">
        <div class="iq-header-title">
          <h4 class="card-title mb-0">Notifications</h4>
          <small class="text-muted">{{ unreadSummary }}</small>
        </div>
        <div class="inbox-header-actions">
          <b-button variant="outline-secondary" size="sm" :disabled="loading" @click="load">
            Refresh
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            :disabled="!canMarkAll"
            title="Marks every notification up to the newest one listed as read"
            @click="markAllRead">
            <b-spinner v-if="marking" small class="mr-1" />
            Mark all read
          </b-button>
        </div>
      </div>

      <div class="iq-card-body">
        <b-form-row class="inbox-filters">
          <b-col cols="12" lg="auto" class="inbox-filter">
            <b-form-checkbox :checked="filters.unreadOnly" switch @change="setFilter({ unreadOnly: $event })">
              Unread only
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" sm="6" lg="2" class="inbox-filter">
            <b-form-select
              :value="filters.category"
              :options="categoryOptions"
              size="sm"
              aria-label="Category"
              @change="setFilter({ category: $event })" />
          </b-col>
          <b-col cols="12" sm="6" lg="2" class="inbox-filter">
            <b-form-select
              :value="filters.severity"
              :options="severityOptions"
              size="sm"
              aria-label="Severity"
              @change="setFilter({ severity: $event })" />
          </b-col>
          <b-col v-if="showCompanyFilter" cols="12" sm="6" lg="3" class="inbox-filter">
            <b-form-select
              :value="filters.compId"
              :options="companyOptions"
              size="sm"
              aria-label="Company"
              @change="setFilter({ compId: $event })" />
          </b-col>
          <b-col v-if="showVesselFilter" cols="12" sm="6" lg="3" class="inbox-filter">
            <b-form-select
              :value="filters.massId"
              :options="vesselOptions"
              size="sm"
              aria-label="Vessel"
              @change="setFilter({ massId: $event })" />
          </b-col>
        </b-form-row>

        <p v-if="hasActiveFilters" class="inbox-hint">
          Mark all read ignores these filters: it marks every notification up to the newest one listed.
        </p>

        <button v-if="hasNewItems" type="button" class="inbox-new-pill" @click="setFilter({ page: 1 })">
          New notifications · Refresh
        </button>

        <div v-if="loading && !items.length" class="inbox-state" role="status">
          <b-spinner small variant="primary" />
          <p>Loading notifications...</p>
        </div>
        <div v-else-if="loadError" class="inbox-state" role="alert">
          <i class="ri-error-warning-line"></i>
          <p>{{ loadError }}</p>
          <b-button variant="outline-primary" size="sm" @click="load">Retry</b-button>
        </div>
        <div v-else-if="!items.length" class="inbox-state" role="status">
          <i class="ri-notification-off-line"></i>
          <p>{{ hasActiveFilters ? 'No notifications match these filters' : 'No notifications' }}</p>
        </div>
        <b-list-group v-else class="inbox-list" :class="{ 'is-busy': loading }">
          <b-list-group-item
            v-for="item in items"
            :key="item.id"
            href="#"
            class="inbox-row"
            :class="{ 'is-static': !item.link }"
            @click.prevent="open(item)">
            <notification-item :item="item" :now-ms="nowMs" />
          </b-list-group-item>
        </b-list-group>

        <div v-if="totalData > pageSize" class="inbox-pagination">
          <b-pagination
            :value="filters.page"
            :total-rows="totalData"
            :per-page="pageSize"
            :limit="3"
            :disabled="loading"
            size="sm"
            class="mb-0 flex-wrap"
            @change="setFilter({ page: $event })" />
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>
import NotificationInboxSvc from '@/services/NotificationInboxSvc';
import NotificationItem from '@/components/notification/NotificationItem.vue';
import getScopedAssets from '@/util/scopedAssets';
import { companyList } from '@/util/companies';
import { isCancelled } from '@/util/master/requestError';
import {
  PAGE_SIZE,
  SEVERITY_META,
  CATEGORY_META,
  emptyFilters,
  applyFilters,
  toInboxParams,
  filtersToQuery,
  queryToFilters,
  maxId,
  markReadUpTo,
  withoutHiddenCompany,
} from '@/util/notificationInbox';

const NOW_TICK_MS = 60 * 1000;

const byText = (a, b) => a.text.localeCompare(b.text);
const metaOptions = (meta, allText) => [
  { value: '', text: allText },
  ...Object.keys(meta).map((value) => ({ value, text: meta[value].label })),
];

export default {
  name: 'NotificationInbox',
  components: { NotificationItem },
  data() {
    return {
      pageSize: PAGE_SIZE,
      filters: emptyFilters(),
      items: [],
      totalData: 0,
      loading: false,
      loadError: '',
      loadSeq: 0,
      baselineUnread: null,
      vessels: [],
      marking: false,
      nowMs: Date.now(),
      categoryOptions: metaOptions(CATEGORY_META, 'All categories'),
      severityOptions: metaOptions(SEVERITY_META, 'All severities'),
    };
  },
  computed: {
    unread() {
      return this.$store.getters['notificationInbox/unread'];
    },
    unreadSummary() {
      const scope = this.showCompanyFilter ? ' across all your companies' : '';
      return `${this.unread} unread${scope}`;
    },
    activeCompId() {
      return this.$store.getters['auth/activeCompany']?.compId;
    },
    companyOptions() {
      const companies = companyList(this.$store.getters['auth/availableCompany'])
        .map((company) => ({ value: company.compId, text: company.compName || `#${company.compId}` }))
        .sort(byText);
      return [{ value: null, text: 'All companies' }, ...companies];
    },
    showCompanyFilter() {
      return this.companyOptions.length > 2;
    },
    vesselOptions() {
      return [{ value: null, text: 'All vessels' }, ...this.vessels];
    },
    // vessel cuma ada buat company aktif
    showVesselFilter() {
      return !this.filters.compId || this.filters.compId === this.activeCompId;
    },
    hasActiveFilters() {
      return filtersToQuery({ ...this.filters, page: 1 }) !== '';
    },
    hasNewItems() {
      return this.baselineUnread !== null && this.unread > this.baselineUnread;
    },
    canMarkAll() {
      return this.unread > 0 && maxId(this.items) !== null && !this.marking && !this.loading;
    },
  },
  created() {
    if (!this.canRead('notification_inbox')) {
      this.$router.replace({ name: 'Forbidden' });
      return;
    }
    const fromQuery = queryToFilters(window.location.search, this.activeCompId);
    this.filters = withoutHiddenCompany(fromQuery, this.showCompanyFilter);
    this.nowTimer = setInterval(() => {
      this.nowMs = Date.now();
    }, NOW_TICK_MS);
    // subscribe biar count yang angkanya sama tetap kepegang
    this.unsubscribeCount = this.$store.subscribe(({ type }, state) => {
      if (type === 'notificationInbox/SET_COUNT') this.trackBaseline(state.notificationInbox.unread);
    });
    this.load();
    this.loadVessels();
  },
  beforeDestroy() {
    // lanjutan load() yang masih jalan jadi basi
    this.loadSeq += 1;
    clearInterval(this.nowTimer);
    if (this.unsubscribeCount) this.unsubscribeCount();
  },
  methods: {
    // patokan cuma diisi sekali atau diturunkan
    trackBaseline(unread) {
      if (this.baselineUnread === null || unread < this.baselineUnread) this.baselineUnread = unread;
    },
    setFilter(patch) {
      const next = applyFilters(this.filters, patch, this.activeCompId);
      this.filters = withoutHiddenCompany(next, this.showCompanyFilter);
      this.load();
    },
    // bukan router.replace, itu ngebatalin request yang jalan
    syncUrl() {
      const { pathname } = window.location;
      window.history.replaceState(window.history.state, '', `${pathname}${filtersToQuery(this.filters)}`);
    },
    async load() {
      this.loadSeq += 1;
      const seq = this.loadSeq;
      this.loading = true;
      this.loadError = '';
      this.syncUrl();
      try {
        const [body, countFresh] = await Promise.all([
          NotificationInboxSvc.getInbox(toInboxParams(this.filters), { cancellable: true }),
          this.$store.dispatch('notificationInbox/fetchCount'),
        ]);
        if (seq !== this.loadSeq) return;
        const lastPage = body.pagination?.totalPage || 1;
        if (!body.data?.length && this.filters.page > lastPage) {
          // halaman kebablasan, URL basi atau data berkurang
          this.filters = { ...this.filters, page: lastPage };
          this.load();
          return;
        }
        this.items = body.data || [];
        this.totalData = body.pagination?.totalData || 0;
        this.nowMs = Date.now();
        // count basi bukan patokan yang sah
        if (countFresh) this.baselineUnread = this.unread;
      } catch (error) {
        if (seq !== this.loadSeq || isCancelled(error)) return;
        console.warn('notification inbox failed:', error?.message);
        this.items = [];
        this.totalData = 0;
        this.loadError = "Couldn't load notifications.";
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    async loadVessels() {
      try {
        const assets = await getScopedAssets();
        this.vessels = assets
          .map((asset) => ({ value: asset.massId, text: asset.massName || `#${asset.massId}` }))
          .sort(byText);
      } catch (error) {
        if (!isCancelled(error)) console.warn('vessel list failed:', error?.message);
      }
    },
    async open(item) {
      const readAt = await this.$store.dispatch('notificationInbox/openItem', { item, router: this.$router });
      if (!readAt) return;
      this.items = markReadUpTo(this.items, { id: item.id, readAt });
    },
    async markAllRead() {
      this.marking = true;
      const beforeId = maxId(this.items);
      const readAt = await this.$store.dispatch('notificationInbox/markAllRead', beforeId);
      this.marking = false;
      if (!readAt) return;
      this.items = markReadUpTo(this.items, { upToId: beforeId, readAt });
    },
  },
};
</script>

<style scoped>
.inbox-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.inbox-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.inbox-filters {
  align-items: center;
}

.inbox-filter {
  margin-bottom: 8px;
}

.inbox-hint {
  margin: 0 0 12px;
  color: #6c757d;
  font-size: 12px;
}

.inbox-new-pill {
  display: block;
  margin: 0 auto 12px;
  padding: 4px 14px;
  border: 0;
  border-radius: 14px;
  background: var(--mz-primary-gradient);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.inbox-state {
  padding: 40px 16px;
  color: #6c757d;
  text-align: center;
}

.inbox-state i {
  display: block;
  margin-bottom: 6px;
  color: #c5cdda;
  font-size: 2rem;
}

.inbox-state p {
  margin: 8px 0 12px;
  font-size: 13px;
}

.inbox-list.is-busy {
  opacity: 0.6;
  pointer-events: none;
}

.inbox-row {
  padding: 12px 14px;
}

.inbox-row.is-static {
  cursor: default;
}

.inbox-pagination {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
</style>
